/* eslint-disable unicorn/consistent-function-scoping */
import { readonly, ref } from 'vue'
import axios from '@axios'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import * as jwt from '@module/shared/constants/auth'

type Permission = {}
type AuthData = {
    id: number
    name: string
    email: string
    isAdmin: 1 | 0
    username: string
    ability: Permission[]
    ruc: string
    empresa: string
    alias: string
    logo: string
    tabs: boolean
}

export const useAuthStore = defineStore('auth', () => {
    const isLoggedIn = ref(false)

    function getAccessToken() {
        return localStorage.getItem(jwt.TOKEN_STORAGE_KEY_NAME)
    }

    function setAccessToken(token: string) {
        localStorage.setItem(jwt.TOKEN_STORAGE_KEY_NAME, token)
        isLoggedIn.value = true
    }

    function removeAccessToken() {
        localStorage.removeItem(jwt.TOKEN_STORAGE_KEY_NAME)
        isLoggedIn.value = false
    }

    function getExpiresAtValue() {
        return localStorage.getItem(jwt.TOKEN_EXPIRE_AT_FIELD_NAME)
    }

    function setExpiresAtValue(value: string) {
        localStorage.setItem(jwt.TOKEN_EXPIRE_AT_FIELD_NAME, value)
    }

    function getWebPushToken() {
        return localStorage.getItem(jwt.WEB_PUSH_TOKEN_STORAGE_KEY_NAME)
    }

    function setWebPushToken(token: string | null) {
        if (token === null) {
            return localStorage.removeItem(jwt.WEB_PUSH_TOKEN_STORAGE_KEY_NAME)
        }
        localStorage.setItem(jwt.WEB_PUSH_TOKEN_STORAGE_KEY_NAME, token)
    }
    function removeWebPushToken() {
        localStorage.removeItem(jwt.WEB_PUSH_TOKEN_STORAGE_KEY_NAME)
    }
    function removeRefreshToken() {
        localStorage.removeItem(jwt.REFRESH_TOKEN_STORAGE_KEY_NAME)
    }

    function removeTokens() {
        removeAccessToken()
        removeRefreshToken()
        removeWebPushToken()
    }

    if (getAccessToken()) {
        isLoggedIn.value = true
    }

    const unsubcribeWebPushToken = async (params: { token: string }) => {
        return await axios.delete('/api/auth/web-push/token', { params })
    }

    const detachWebPushToken = () => {
        const token = getWebPushToken()
        setWebPushToken(null)
        token && unsubcribeWebPushToken({ token })
    }

    const logout = async () => {
        try {
            return await axios.post('/api/auth/logout')
        } finally {
            localStorage.removeItem('userData')
            localStorage.removeItem('empresa')
            localStorage.removeItem(jwt.WEB_PUSH_TOKEN_STORAGE_KEY_NAME)
            localStorage.removeItem(jwt.TOKEN_STORAGE_KEY_NAME)
        }
    }

    const authData = useLocalStorage<AuthData | null>('userData', null, {
        serializer: { read: JSON.parse, write: JSON.stringify },
    })
    return {
        isLoggedIn: readonly(isLoggedIn),
        authData,
        getAccessToken,
        setAccessToken,
        setExpiresAtValue,
        getExpiresAtValue,
        getWebPushToken,
        setWebPushToken,
        removeAccessToken,
        removeTokens,
        detachWebPushToken,
        logout,
    }
})
